import { Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { isRightPanelDragging } from '@redact/components/MediaDetailsPage/Editor/rightPanelDragState';

/**
 * Emits when an element's layout width changes. Prefer this over window resize
 * listeners for tracking element width changes.
 *
 * While the right panel is being drag-resized, per-pixel emissions are
 * suppressed (re-rendering the Konva timelines every mousemove made the drag
 * lag); the drag fires a window resize event on release, which re-emits the
 * final width exactly once.
 */
export function observeElementWidth(element: HTMLElement): Observable<number> {
  return new Observable<number>((subscriber) => {
    const emit = (width = element.clientWidth) => subscriber.next(width);

    emit();

    const onWin = () => {
      if (!isRightPanelDragging()) {
        emit();
      }
    };
    window.addEventListener('resize', onWin);

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        if (isRightPanelDragging()) {
          return;
        }
        const entry = entries[0];
        emit(entry ? Math.round(entry.contentRect.width) : element.clientWidth);
      });
      ro.observe(element);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', onWin);
      };
    }

    return () => window.removeEventListener('resize', onWin);
  }).pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
